import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  extraFieldEntries,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  diagnosisEntries,
  assetEntries,
  serviceOrderInfoEntries,
  formatPaymentMethods,
  renderSignatureSection,
  renderFooter,
  clientDocumentLabel,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

/**
 * "Compacto" template — optimized for many items.
 * Single-line header, tiny font table, inline total, no photos, no PIX section.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;
  const { primaryColor, grayLight, grayText, darkText, white } =
    buildPalette(data.brandColorRGB);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === COMPACT HEADER (single line strip) ===
  const headerHeight = 12;
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');
  doc.setTextColor(...white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);

  const companyName = data.company?.name || 'Empresa';
  const docTitle = resolveDocumentTitle(data);
  const dateText = formatQuoteDate(data.quoteDate);

  // Logo tiny
  let leftPad = margin;
  if (logoBase64) {
    try {
      const logoSize = 8;
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, margin, 2, logoSize, logoSize);
      leftPad = margin + logoSize + 3;
    } catch {
      /* ignore */
    }
  }

  doc.text(companyName, leftPad, 8);

  // A faixa tem UMA linha com tres textos: empresa (esq.), titulo (centro) e
  // data (dir.). O titulo e centralizado na pagina, entao a largura que ele pode
  // ocupar e o DOBRO da menor folga ate um dos vizinhos - medida com os textos
  // ja desenhados, e nao estimada, porque o nome da empresa e livre.
  const centro = pageWidth / 2;
  const folgaEsquerda = centro - (leftPad + doc.getTextWidth(companyName) + 3);
  const folgaDireita = pageWidth - margin - doc.getTextWidth(dateText) - 3 - centro;
  const tituloCabecalho = fitHeaderTitle(
    doc,
    docTitle,
    Math.max(0, Math.min(folgaEsquerda, folgaDireita)) * 2,
    10,
    { minSize: 6 },
  );
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, centro, 8, { align: 'center' });

  doc.setFontSize(10);
  doc.text(dateText, pageWidth - margin, 8, { align: 'right' });

  let yPos = headerHeight + 4;
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: pageWidth / 2, y: yPos - 4.6, maxWidth: contentWidth,
    color: grayText, align: 'center', size: 8,
  });

  // === CLIENT LINE (super compact) ===
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...darkText);
  doc.text(`Cliente: ${data.client.name || 'Não informado'}`, margin, yPos);

  const rightBits: string[] = [];
  if (data.client.phone) rightBits.push(`Tel ${data.client.phone}`);
  if (data.client.document) rightBits.push(clientDocumentLabel(data.client.document));
  if (data.validityDays && data.quoteDate) {
    rightBits.push(`Val. ${calcValidityDate(data.quoteDate, data.validityDays)}`);
  }
  if (data.reference) rightBits.push(`Ref. ${data.reference}`);
  if (rightBits.length) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);
    doc.text(rightBits.join('  ·  '), pageWidth - margin, yPos, { align: 'right' });
  }

  if (data.client.address) {
    yPos += 3.5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);
    const line = doc.splitTextToSize(`End. ${data.client.address}`, contentWidth)[0];
    doc.text(line, margin, yPos);
  }

  yPos += 5;

  // Thin separator
  doc.setDrawColor(...grayLight);
  doc.setLineWidth(0.2);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 3;

  // === ITEMS TABLE (7pt, dense) ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 12, halign: 'center' as const },
        2: { cellWidth: 10, halign: 'center' as const },
        3: { cellWidth: 22, halign: 'right' as const },
        4: { cellWidth: 16, halign: 'center' as const },
        5: { cellWidth: 24, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 14, halign: 'center' as const },
        2: { cellWidth: 10, halign: 'center' as const },
        3: { cellWidth: 26, halign: 'right' as const },
        4: { cellWidth: 26, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: primaryColor,
      textColor: white,
      fontStyle: 'bold',
      fontSize: 7,
      cellPadding: 2,
      halign: 'center',
    },
    bodyStyles: { fontSize: 7, cellPadding: 1.6, textColor: darkText },
    columnStyles,
    styles: { lineColor: [220, 220, 220], lineWidth: 0.2 },
    margin: { left: margin, right: margin },
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 3;

  // === INLINE TOTAL ===
  const totals = pdfQuoteTotals(data);
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: 45,
  });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...grayText);
  doc.text('TOTAL', pageWidth - margin - 45, yPos + 4);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...primaryColor);
  doc.text(formatCurrency(totals.total), pageWidth - margin, yPos + 4, { align: 'right' });

  yPos += 10;

  // === OBSERVATIONS (compact) ===
  const parts: string[] = [];
  // O compacto não tem bloco de PIX nem seção própria de pagamento: cabe tudo
  // na mesma lista densa, que já trunca sozinha para não passar da página.
  const acceptedMethods = formatPaymentMethods(data.paymentMethods);
  if (acceptedMethods) parts.push(`Formas aceitas: ${acceptedMethods}`);
  if (data.paymentTerms) parts.push(`Condições: ${data.paymentTerms}`);
  // Equipamento, responsáveis e diagnóstico da OS: no compacto tudo entra na
  // MESMA lista densa (o template não tem seções próprias), com o rótulo de
  // cada campo à frente. A ordem é a do documento: o que foi recebido, quem
  // atendeu, e só então o laudo.
  for (const entry of assetEntries(data.serviceOrderAsset)) parts.push(`${entry.label}: ${entry.text}`);
  for (const entry of serviceOrderInfoEntries(data)) parts.push(`${entry.label}: ${entry.text}`);
  for (const entry of diagnosisEntries(data.diagnosis)) parts.push(`${entry.label}: ${entry.text}`);
  // Campos extras do prestador (L5): no compacto eles entram na MESMA lista
  // densa, como o diagnóstico — o template não tem seções próprias.
  for (const extra of extraFieldEntries(data)) parts.push(`${extra.label}: ${extra.value}`);
  if (data.warranty) parts.push(`Garantia: ${data.warranty}`);
  if (data.notes) parts.push(`Obs.: ${data.notes}`);
  if (data.termsConditions) parts.push(`Termos: ${data.termsConditions}`);

  if (parts.length && yPos < pageHeight - 45) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);
    for (const p of parts) {
      const lines = doc.splitTextToSize(p, contentWidth);
      const remaining = pageHeight - 45 - yPos;
      const maxLines = Math.max(1, Math.floor(remaining / 3.2));
      const trimmed = lines.slice(0, maxLines);
      doc.text(trimmed, margin, yPos);
      yPos += 3.2 * trimmed.length + 1.5;
      if (yPos >= pageHeight - 45) break;
    }
  }

  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}
